import { Box, Icon, IconButton, Stack, SxProps, Theme, Tooltip, Typography } from '@mui/material';
import { DATE_FORMAT_SHORT_FNS, MessageType, RootEntityType } from '@schooly/api';
import { Message } from '@schooly/api';
import { AvatarAuth } from '@schooly/components/avatar-auth';
import { ThreadUnread, useThreadUnread } from '@schooly/components/threads';
import { ClipIcon, DoubleCheckIcon, ListRow, TypographyWithOverflowHint } from '@schooly/style';
import { getUserFullName } from '@schooly/utils/get-user-full-name';
import { format } from 'date-fns';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { retrieveApiDate } from '../../../helpers/misc';

export enum ListViewMessagesRowVariant {
  Profile = 'profile',
  Group = 'group',
}

export interface ListViewMessagesRowProps {
  message: Message;
  groupId?: string;
  sx?: SxProps<Theme>;
  isProfileView: boolean;
}

export const ListViewMessagesRow: React.FC<ListViewMessagesRowProps> = ({
  message,
  groupId,
  isProfileView,
  sx,
}) => {
  const navigate = useNavigate();
  const { publish_date, title, id, attachments, creator, type } = message;

  const publishDate = publish_date && retrieveApiDate(publish_date);

  const unread = useThreadUnread({
    rootEntityType: RootEntityType.MESSAGE,
    rootEntityId: message.id,
  });

  const attachmentsIcon =
    attachments && attachments.length > 0 ? (
      <Icon>
        <ClipIcon />
      </Icon>
    ) : null;

  return (
    <ListRow
      title={
        <Stack direction="row" alignItems="center" gap={0.75} minWidth={0}>
          <Box width={20} flexShrink={0}>
            {type === MessageType.Thread ? <ThreadUnread unread={unread} /> : null}
          </Box>
          <Typography noWrap width={44} color="common.grey" className="list-row-title">
            {publishDate ? format(publishDate, DATE_FORMAT_SHORT_FNS).toUpperCase() : '-'}
          </Typography>
          {creator && (
            <Tooltip title={getUserFullName(creator)} placement="bottom">
              <Box ml={3.25}>
                <AvatarAuth user={creator} />
              </Box>
            </Tooltip>
          )}
          <TypographyWithOverflowHint variant="h3" pl={2.5}>
            {title}
          </TypographyWithOverflowHint>
        </Stack>
      }
      actions={
        <Stack direction="row" justifyContent="flex-end" gap={1} pr={1}>
          {attachmentsIcon}
          {/* Check icon shows if the message is read/unread by the recipient only in profile view */}
          {isProfileView ? (
            <IconButton
              inverse={message.recipient_read}
              sx={{
                color: message.recipient_read ? 'success.main' : 'text.secondary',
              }}
            >
              <DoubleCheckIcon />
            </IconButton>
          ) : null}
        </Stack>
      }
      onClick={() => navigate(`/messages/${id}${groupId ? `?group_id=${groupId}` : ''}`)}
      sx={sx}
    />
  );
};
